import Split from 'react-split';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateTimeOriginal } from './utils/exif';
import { Gallery } from './Gallery';
import { Preview } from './Preview';
import { copySelected } from './copySelected';
import Header from './components/Header';
import Footer from './layout/Footer';
import { trackEvent } from '@/utils/analytics';
import { isSupportedImageFileName } from '@/core/fileFilters';
import { togglePicked } from '@/core/picked';
import { getNextSelectedIndex } from '@/core/navigation';
import { OPEN_FOLDER_ON_ENTER_APP } from '@/constants/sessionKeys';

type SortMode = 'name' | 'date';

export default function App() {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const [handles, setHandles] = useState<FileSystemFileHandle[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<number | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('name');
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const sortHandles = async (list: FileSystemFileHandle[], mode: SortMode) => {
    if (mode === 'name') {
      list.sort((a, b) =>
        a.name.localeCompare(b.name, 'ja-JP', { numeric: true, sensitivity: 'base' }),
      );
      return;
    }

    const recs: { h: FileSystemFileHandle; ts: number }[] = [];
    for (const h of list) {
      const f = await h.getFile();
      let ts = f.lastModified;
      const tag = await getDateTimeOriginal(f);
      if (tag) {
        const iso = tag.replace(/^(\d{4}):(\d{2}):(\d{2}) /, '$1-$2-$3T');
        ts = new Date(iso).getTime();
      }
      recs.push({ h, ts });
    }
    recs.sort((a, b) => a.ts - b.ts);
    list.splice(0, list.length, ...recs.map((r) => r.h));
  };

  const openFolder = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dir = await (window as any).showDirectoryPicker({ mode: 'read' });
      const hs: FileSystemFileHandle[] = [];
      for await (const handle of dir.values()) {
        if (handle.kind === 'file' && isSupportedImageFileName(handle.name)) {
          hs.push(handle);
        }
      }
      await sortHandles(hs, sortMode);
      setHandles(hs);
      setPicked(new Set());
      setSelected(null);
      trackEvent('open_folder', {
        method: 'directoryPicker',
        file_count: hs.length,
      });
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return;
      throw err;
    }
  };

  const loadFiles = async (files: File[]) => {
    const hs = files.map(
      (f) =>
        ({
          kind: 'file',
          name: f.name,
          getFile: async () => f,
        }) as unknown as FileSystemFileHandle,
    );
    await sortHandles(hs, sortMode);
    setHandles(hs);
    setPicked(new Set());
    setSelected(null);
    trackEvent('open_folder', {
      method: 'dragAndDrop',
      file_count: hs.length,
    });
  };

  const toggleSort = async () => {
    const nextSortMode = sortMode === 'name' ? 'date' : 'name';
    const sorted = [...handles];

    setSortMode(nextSortMode);
    await sortHandles(sorted, nextSortMode);
    setHandles(sorted);

    showToast(nextSortMode === 'name' ? t('sorted-name') : t('sorted-date'));
    trackEvent('toggle_sort', {
      mode: nextSortMode,
    });
  };

  useEffect(() => {
    if (sessionStorage.getItem(OPEN_FOLDER_ON_ENTER_APP) !== '1') return;
    sessionStorage.removeItem(OPEN_FOLDER_ON_ENTER_APP);
    void openFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!handles.length) return;

      if (e.key === ' ' || e.key === 'c' || e.key === 'C') {
        if (selected === null) return;
        setPicked((current) => togglePicked(current, selected));
        e.preventDefault();
        return;
      }

      const columns = Math.max(1, Math.floor(window.innerWidth * 0.7 / 208));
      const next = getNextSelectedIndex({
        key: e.key,
        currentIndex: selected,
        total: handles.length,
        columns,
      });
      if (next === null) return;

      setSelected(next);
      e.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handles.length, selected]);

  return (
    <>
      {toast && <div className="toast-container toast-enter">{toast}</div>}

      <Header
        onOpenFolder={openFolder}
        onToggleSort={toggleSort}
        onCopy={async () => {
          await copySelected([...picked].map((i) => handles[i]));
          showToast(
            t('copied-count', {
              count: picked.size,
              formattedCount: nf.format(picked.size),
            }),
          );
        }}
        pickedCount={picked.size}
        sortMode={sortMode}
      />


      <main className="main-area">
        <section className="app-status-bar">
          <span>{t('status-total', { count: handles.length })}</span>
          <span>{t('status-picked', { count: picked.size })}</span>
          <span>{t('status-shortcut')}</span>
        </section>
        <Split className="split" sizes={[30, 70]} minSize={[380, 320]} gutterSize={6}>
          <div className="gallery-pane" style={{ minWidth: 0 }}>
            <Gallery
              files={handles}
              onFiles={(files) => loadFiles(files)}
              picked={picked}
              selected={selected}
              onToggle={(idx) => setPicked((current) => togglePicked(current, idx))}
              onSelect={setSelected}
            />
          </div>

          <div className="preview-pane" style={{ minWidth: 0, borderLeft: '1px solid var(--ls-border)' }}>
            <Preview
              file={selected !== null ? handles[selected] : null}
              pickedCurrent={selected !== null && picked.has(selected)}
              onToggleCurrent={() => {
                if (selected === null) return;
                setPicked((current) => togglePicked(current, selected));
              }}
            />
          </div>
        </Split>
      </main>

      <Footer />
    </>
  );
}
